import { A11yModule } from '@angular/cdk/a11y';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DOCUMENT, Location } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  type Signal,
  TemplateRef,
  untracked,
  ViewContainerRef,
  viewChild,
  ViewEncapsulation,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationSkipped,
  Router,
  RouterLink,
  RouterOutlet,
  UrlSegmentGroup,
} from '@angular/router';
import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { AccountService } from '@frontendlib/common/services/account.service';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { SpaceStateService } from '@modules/common/services/space-state.service';
import { Space } from '@modules/settings/types/space';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  SETTINGS_DISCARD_CONFIRMATION,
  SettingsDiscardGate,
  type SettingsDirtyPage,
} from './account/settings-subpage.guard';
import { SettingsConfirmService } from './kit/settings-confirm.service';
import { settingsMenu, shippedSettingsPages } from './settings-menu';
import { SettingsPageHeaderComponent } from './settings-page-header/settings-page-header.component';
import { SettingsFocusService } from './settings-focus.service';
import {
  resolveSettingsAudience,
  resolveSettingsRole,
  SETTINGS_WINDOW_OPENED_IN_APP_STATE,
  SettingsWindowOpenerService,
} from './settings-window-opener.service';

const SETTINGS_OUTLET = 'settings';

interface SettingsPageHeaderAction {
  readonly label: string;
  readonly activate: () => void;
  /** Drawn but inert, for a page whose action exists yet cannot act right now. */
  readonly disabled?: boolean;
}

interface SettingsWindowPage extends SettingsDirtyPage {
  readonly headerAction: Signal<SettingsPageHeaderAction | null>;
  /** The page title, for a page whose title changes with its state. */
  readonly headerTitle: Signal<string>;
  /** The line under the page title, for a page that has one of its own. */
  readonly headerSubtitle: Signal<string>;
}

/*
 * Both halves arrive asynchronously and independently: the space from SpaceStateService, the
 * account from AccountService's filtered BehaviorSubject, which emits nothing until its GET
 * returns. With an unknown account id the participant lookup matches nobody and the ternary
 * chain below has no not-found branch, so the Owner reloading inside a team was painted — and
 * given the menu of — a Member. Draw no role until both halves are known.
 */
export function resolveSettingsAudienceLabel(
  insideTeam: boolean,
  space: Space | null | undefined,
  accountId: string,
): string {
  if (!insideTeam) {
    return 'Personal Workspace';
  }
  const role = resolveSettingsRole(space, accountId);
  const roleLabel = role ? role[0].toUpperCase() + role.slice(1) : '';
  return [space?.title ?? 'Team Workspace', roleLabel].filter(Boolean).join(' · ');
}

@Component({
  selector: 'app-settings-window',
  imports: [A11yModule, IconComponent, RouterLink, RouterOutlet, SettingsPageHeaderComponent],
  templateUrl: './settings-window.component.html',
  styleUrls: ['./settings-window.component.less', './kit/settings-kit.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class SettingsWindowComponent {
  private readonly accountService = inject(AccountService);
  /** The Team workspace pages are a team login's: a personal login gets the personal window inside any space. */
  private readonly accountKind = inject(AccountKindService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly confirmation = inject(SettingsConfirmService);
  private readonly discardGate = inject(SettingsDiscardGate);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly location = inject(Location);
  private readonly overlay = inject(Overlay);
  private readonly router = inject(Router);
  private readonly spaceContextStore = inject(SpaceContextStore);
  private readonly spaceStateService = inject(SpaceStateService);
  private readonly focus = inject(SettingsFocusService);
  private readonly opener = inject(SettingsWindowOpenerService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly windowPortal = viewChild.required<TemplateRef<unknown>>('windowPortal');

  private readonly account = toSignal(this.accountService.getAccount(), { initialValue: null });
  private readonly space = toSignal(this.spaceStateService.getCurrentSpace(), { initialValue: undefined });

  // The state survives reloads but is absent on a pasted URL, which is exactly the distinction close needs.
  private readonly openedInApp = window.history.state?.[SETTINGS_WINDOW_OPENED_IN_APP_STATE] === true;

  // The menu item is transient. Its persistent trigger is the only useful focus target after routing removes us.
  private overlayRef: OverlayRef | undefined;
  private historyDepth = 0;
  private sawInitialNavigation = false;
  private initialFocusPending = true;
  // The section whose app has been opened already, so a later menu refresh cannot undo a person's collapse.
  private revealedSection = '';

  /*
   * The overlay outlives the gesture. Routing away is what destroys this component, several async
   * turns after the click, so the veil is still live for a second click and Escape auto-repeats
   * while it is held — and two closes would pop two history entries, landing the person one page
   * behind where they opened the window. Same class of harm as the blank tab the cold-paste branch
   * below exists to prevent.
   */
  private closing = false;
  private askingToClose = false;
  // Set from the outlet's own (activate) output, so the shell can ask the live page before it moves history.
  private readonly activePage = signal<Partial<SettingsWindowPage> | null>(null);

  protected readonly railSubtitle = computed(() =>
    resolveSettingsAudienceLabel(
      this.spaceContextStore.insideTeam() && this.accountKind.isTeam(),
      this.space(),
      this.account()?.id ?? '',
    ),
  );
  protected readonly menu = computed(() => {
    const audience = resolveSettingsAudience(
      this.spaceContextStore.insideTeam() && this.accountKind.isTeam(),
      this.space(),
      this.account()?.id ?? '',
    );
    return audience
      ? settingsMenu(
          audience,
          shippedSettingsPages(audience, this.accountKind.isTeam()),
          // A limited account keeps its app settings (auto-reply, permissions, blocklist): only creating is paused.
          (app) => this.opener.canSeeApp(app),
        )
      : [];
  });
  protected readonly openGroups = signal(new Set(['Account', 'Team workspace', 'Apps', 'My apps', 'Team apps']));
  /** Keyed by the app row's path: My apps and Team apps each have a Mail row, opened and closed on its own. */
  protected readonly openApps = signal(new Set<string>());
  protected readonly pageTitle = signal('Settings');
  protected readonly heading = computed(() => this.activePage()?.headerTitle?.() || this.pageTitle());
  protected readonly headerAction = computed<SettingsPageHeaderAction | null>(
    () => this.activePage()?.headerAction?.() ?? null,
  );
  protected readonly selectedSection = signal('');
  /*
   * From the route's own `data`, never from the title string: the title is copy, and a future section
   * called `Account` would otherwise take the Back link and the focus rule with it (review F6.1).
   */
  private readonly routedSubpage = signal(false);
  protected readonly isRoutedSubpage = computed(() => this.routedSubpage());
  protected readonly isAccountSubpage = computed(
    () => this.isRoutedSubpage() && this.selectedSection() === 'general/account',
  );
  protected readonly isAccountPage = computed(
    () => this.selectedSection() === 'general/account' && !this.routedSubpage(),
  );
  protected readonly pageSubtitle = computed(
    () =>
      this.activePage()?.headerSubtitle?.() ||
      (this.isAccountPage() ? (this.account()?.email ?? '') : this.isAccountSubpage() ? 'Account' : ''),
  );
  protected readonly backLabel = computed(() => {
    if (!this.isRoutedSubpage()) {
      return '';
    }
    const labels: Record<string, string> = {
      'general/account': 'Account',
      'general/billing': 'Billing',
      'team/billing': 'Billing',
      'mail/import': 'Import',
      'mail/signatures': 'Signatures',
      'mail/templates': 'Templates',
    };
    // My apps' sub-pages (`my/mail/import`) go back to the same section as Apps' own.
    const section = this.selectedSection().replace(/^my\//, '');
    return `Back to ${labels[section] ?? 'Settings'}`;
  });

  private readonly attachWindow = afterNextRender(() => this.open());

  constructor() {
    effect(() => {
      const menuReady = this.menu().length > 0 && !!this.selectedSection();
      if (menuReady && this.initialFocusPending && this.overlayRef) {
        setTimeout(() => this.focusInitialRoute());
      }
    });
    // A link opened while the workspace loads meets an empty menu; the app can only be found once it is ready.
    effect(() => {
      this.menu();
      untracked(() => this.revealSelectedApp());
    });
    this.destroyRef.onDestroy(() => {
      this.initialFocusPending = false;
      this.overlayRef?.dispose();
      this.document.querySelector<HTMLElement>('button[aria-label="Switch workspace"]')?.focus();
    });
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationCancel | NavigationEnd | NavigationSkipped =>
            event instanceof NavigationCancel || event instanceof NavigationEnd || event instanceof NavigationSkipped,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        /*
         * A close the discard guard refused leaves the window standing, so the re-entrancy flag has
         * to come back off or ×, Escape and the veil are dead for the rest of the session, and a
         * grant nobody spent has to be handed back so it cannot wave a later navigation through.
         */
        if (!(event instanceof NavigationEnd)) {
          this.closing = false;
          this.discardGate.revoke();
          return;
        }
        this.readRouteMetadata();
        setTimeout(() => this.focusForRoute());
        if (!this.sawInitialNavigation) {
          this.sawInitialNavigation = true;
          return;
        }
        const navigation = this.router.getCurrentNavigation();
        if (navigation?.trigger === 'popstate') {
          this.historyDepth = Math.max(0, this.historyDepth - 1);
          return;
        }
        // A replaced entry is not a new one: counting it would make close() pop one page too many.
        if (!navigation?.extras?.replaceUrl) {
          this.historyDepth = this.historyDepth + 1;
        }
      });
  }

  protected groupOpen(label: string): boolean {
    return this.openGroups().has(label);
  }

  protected toggleGroup(label: string): void {
    this.openGroups.update((groups) => {
      const next = new Set(groups);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  }

  protected appOpen(appPath: string): boolean {
    return this.openApps().has(appPath);
  }

  protected toggleApp(appPath: string): void {
    this.openApps.update((apps) => {
      const next = new Set(apps);
      next.has(appPath) ? next.delete(appPath) : next.add(appPath);
      return next;
    });
  }

  protected isCurrent(path: string): boolean {
    return this.selectedSection() === path;
  }

  /*
   * The Back affordance REPLACES the entry the sub-page pushed rather than stacking a second one on
   * top of it, so the browser's Back no longer re-enters the page just left (review F6.4). It stays
   * an imperative navigation, not a `location.back()`: a discard the guard refuses then costs the
   * URL only, never the history position — which is the whole of F2.
   */
  protected backToSection(): void {
    void this.router.navigate([this.selectedSection()], { relativeTo: this.activatedRoute, replaceUrl: true });
  }

  protected close(): void {
    if (this.closing || this.askingToClose) {
      return;
    }
    void this.closeWhenAllowed();
  }

  /** Closes the tenant-scoped window before leaving a workspace that has just been deleted. */
  public closeAfterWorkspaceDelete(): void {
    this.commitClose();
    this.spaceContextStore.resetToPersonal(this.account()?.id);
  }

  /** Returns an injector that survives Settings, after its own close and discard gate. */
  public async closeForRecord(): Promise<Injector | null> {
    if (this.closing || this.askingToClose) return null;
    // The space context is above this routed component and survives its destruction.
    const parent = this.viewContainerRef.injector.get(Injector, null, { skipSelf: true });
    if (!parent) return null;
    const hasSettings = (group: UrlSegmentGroup): boolean =>
      SETTINGS_OUTLET in group.children || Object.values(group.children).some(hasSettings);
    let finish!: (injector: Injector | null) => void;
    const closed = new Promise<Injector | null>((resolve) => {
      finish = resolve;
    });
    // Do not bind this listener to the destroyed window: NavigationEnd follows deactivation.
    const subscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationCancel || event instanceof NavigationSkipped) {
        subscription.unsubscribe();
        finish(null);
      } else if (event instanceof NavigationEnd) {
        subscription.unsubscribe();
        finish(hasSettings(this.router.parseUrl(event.urlAfterRedirects).root) ? null : parent);
      }
    });
    try {
      await this.closeWhenAllowed();
    } catch (error) {
      subscription.unsubscribe();
      throw error;
    }
    if (!this.closing) {
      subscription.unsubscribe();
      return null;
    }
    return closed;
  }

  protected onPageActivated(page: Partial<SettingsWindowPage>): void {
    this.activePage.set(page);
  }

  protected onPageDeactivated(): void {
    this.activePage.set(null);
  }

  /*
   * Ask first, move history second. `historyGo` IS the navigation that runs the discard guard, and
   * Angular's default `canceledNavigationResolution: 'replace'` puts the URL back on a refusal but
   * not the position — so firing it before the answer parks the person entries away from where they
   * were, with stale forward entries above. The gate carries this answer into the guard so the same
   * gesture is never questioned twice.
   */
  private async closeWhenAllowed(): Promise<void> {
    const page = this.activePage();
    if (page?.hasUnsavedChanges?.()) {
      this.askingToClose = true;
      const discard = await firstValueFrom(this.confirmation.confirm({ ...SETTINGS_DISCARD_CONFIRMATION }));
      this.askingToClose = false;
      if (!discard) {
        return;
      }
      this.discardGate.grant();
    }
    this.commitClose();
  }

  private commitClose(): void {
    this.closing = true;

    if (this.openedInApp) {
      this.historyDepth ? this.location.historyGo(-(this.historyDepth + 1)) : this.location.back();
      return;
    }

    const url = this.router.parseUrl(this.router.url);
    this.removeSettingsOutlet(url.root);
    void this.router.navigateByUrl(url, { replaceUrl: true });
  }

  private open(): void {
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'settings-window-backdrop',
      panelClass: 'settings-window-overlay',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });

    // This ViewContainerRef belongs to the named outlet under *spaceContext, preserving its scoped providers.
    this.overlayRef.attach(new TemplatePortal(this.windowPortal(), this.viewContainerRef));
    this.overlayRef.backdropClick().subscribe((event) => {
      const passthrough = this.passthroughUnder(event);
      if (passthrough) {
        passthrough.click();
        return;
      }
      this.close();
    });
    this.readRouteMetadata();
    setTimeout(() => this.focusInitialRoute());
  }

  /** The `data-settings-window-passthrough` control under a veil click, which gets the click instead of a close. */
  private passthroughUnder(event: MouseEvent): HTMLElement | null {
    // Absent in jsdom.
    if (typeof this.document.elementsFromPoint !== 'function') {
      return null;
    }
    for (const element of this.document.elementsFromPoint(event.clientX, event.clientY)) {
      const target = element.closest('[data-settings-window-passthrough]');
      if (target instanceof HTMLElement) {
        return target;
      }
    }
    return null;
  }

  private readRouteMetadata(): void {
    let route: ActivatedRoute | null = this.activatedRoute;
    while (route?.firstChild) {
      route = route.firstChild;
    }
    this.pageTitle.set(String(route?.snapshot?.data['title'] ?? 'Settings'));
    this.selectedSection.set(String(route?.snapshot?.data['section'] ?? ''));
    this.routedSubpage.set(route?.snapshot?.data['subpage'] === true);
    this.revealedSection = '';
    this.revealSelectedApp();
  }

  /** An app's pages stay in view while one of them is open, however the window was entered. */
  private revealSelectedApp(): void {
    const section = this.selectedSection();
    if (!section || section === this.revealedSection) {
      return;
    }
    const app = this.menu()
      .flatMap(({ rows }) => rows)
      .find((row) => row.kind === 'app' && row.children?.some((child) => child.path === section));
    if (!app) {
      return;
    }
    this.revealedSection = section;
    if (!this.appOpen(app.path)) {
      this.toggleApp(app.path);
    }
  }

  private focusForRoute(): void {
    const pane = this.overlayRef?.overlayElement;
    if (!pane) {
      return;
    }
    if (this.isRoutedSubpage()) {
      pane
        .querySelector<HTMLElement>(
          '.settings-window-page router-outlet + * input:not([type="hidden"]), .settings-window-page router-outlet + * button',
        )
        ?.focus();
      return;
    }
    if (this.isAccountPage()) {
      const returnFocusLabel = this.focus.consume();
      const target = Array.from(pane.querySelectorAll<HTMLButtonElement>('.settings-window-page button')).find(
        (button) => button.textContent?.trim() === returnFocusLabel,
      );
      if (target) {
        target.focus();
        return;
      }
    }
    pane.querySelector<HTMLElement>('.settings-window-menu-row-selected')?.focus();
  }

  private focusInitialRoute(): void {
    if (!this.initialFocusPending || !this.menu().length || !this.selectedSection()) {
      return;
    }
    const pane = this.overlayRef?.overlayElement;
    const target = this.isRoutedSubpage()
      ? pane?.querySelector<HTMLElement>(
          '.settings-window-page router-outlet + * input:not([type="hidden"]), .settings-window-page router-outlet + * button',
        )
      : pane?.querySelector<HTMLElement>('.settings-window-menu-row-selected');
    if (!target) {
      setTimeout(() => this.focusInitialRoute());
      return;
    }
    this.initialFocusPending = false;
    target.focus();
  }

  private removeSettingsOutlet(group: UrlSegmentGroup): void {
    delete group.children[SETTINGS_OUTLET];
    Object.values(group.children).forEach((child) => this.removeSettingsOutlet(child));
  }
}
